import { CourseFeatures } from "@onetutor/shared/type";
import ChatPage from "@pages/chat";
import CoursePage from "@pages/course";
import CourseEnrollmentPage from "@pages/course-enrollment";
import { CourseManagementPage } from "@pages/course-management";
import DashboardPage from "@pages/dashboard";
import Homepage from "@pages/home";
import ImprintPage from "@pages/imprint";
import LoginPage from "@pages/login";
import NotificationManagementPage from "@pages/notification-management";
import QuizPage from "@pages/quiz";
import QuizSessionPage from "@pages/quiz/QuizSessionPage";
import QuizManagementPage from "@pages/quiz-management";
import SurveyPage from "@pages/survey";
import UniversityManagementPage from "@pages/university-management";
import UserManagementPage from "@pages/user-management";
import UserProfilePage from "@pages/user-profile";
import * as Sentry from "@sentry/react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useRouteError,
} from "react-router";
import PrivacyConsentPage from "../pages/privacy/PrivacyConsentPage";
import PrivacyPage from "../pages/privacy/PrivacyPage";
import { RequireAuth } from "./guards/RequireAuth";
import { RequireFeature } from "./guards/RequireFeature";
import { RequirePrivacyConsent } from "./guards/RequirePrivacyConsent";
import {
  RequireAdmin,
  RequireLecturer,
  RequireManager,
} from "./guards/RequireRole";
import { RequireSurvey } from "./guards/RequireSurvey";

const wrapCreateBrowserRouterWithNoBoundary =
  (fn: typeof createBrowserRouter): typeof createBrowserRouter =>
  (routes, options) =>
    fn(
      [
        {
          path: "/*",
          ErrorBoundary: () => {
            throw useRouteError();
          },
          children: routes,
        },
      ],
      options,
    );

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(
  wrapCreateBrowserRouterWithNoBoundary(createBrowserRouter),
);

export const router = sentryCreateBrowserRouter([
  {
    path: "login",
    element: <LoginPage />,
  },
  {
    path: "privacy",
    element: <PrivacyPage />,
  },
  {
    path: "imprint",
    element: <ImprintPage />,
  },
  {
    path: "privacyConsent",
    element: <PrivacyConsentPage />,
  },
  {
    element: (
      <RequireAuth>
        <RequirePrivacyConsent>
          <Outlet />
        </RequirePrivacyConsent>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Homepage /> },
      {
        path: "profile",
        element: <UserProfilePage />,
      },
      {
        path: "course/:courseId/*",
        element: (
          <RequireSurvey>
            <Outlet />
          </RequireSurvey>
        ),
        children: [
          { index: true, element: <CoursePage /> },
          {
            path: "chat",
            element: (
              <RequireFeature feature={CourseFeatures.CHAT}>
                <ChatPage />
              </RequireFeature>
            ),
          },
          {
            path: "chat/:sessionId",
            element: (
              <RequireFeature feature={CourseFeatures.CHAT}>
                <ChatPage />
              </RequireFeature>
            ),
          },
          {
            path: "quiz",
            element: (
              <RequireFeature feature={CourseFeatures.QUIZ}>
                <QuizPage />
              </RequireFeature>
            ),
          },
          {
            path: "quiz/session",
            element: (
              <RequireFeature feature={CourseFeatures.QUIZ}>
                <QuizSessionPage />
              </RequireFeature>
            ),
          },
          { path: "enroll", element: <CourseEnrollmentPage /> },
          {
            path: "survey",
            element: <SurveyPage />,
          },
          {
            path: "manage",
            element: (
              <RequireManager>
                <Outlet />
              </RequireManager>
            ),
            children: [
              { index: true, element: <CourseManagementPage /> },
              { path: "quiz", element: <QuizManagementPage /> },
              { path: "dashboard", element: <DashboardPage /> },
            ],
          },
        ],
      },
      {
        path: "course/create",
        element: (
          <RequireLecturer>
            <CourseManagementPage />
          </RequireLecturer>
        ),
      },
    ],
  },
  {
    path: "admin",
    element: (
      <RequireAuth>
        <RequireAdmin>
          <Outlet />
        </RequireAdmin>
      </RequireAuth>
    ),
    children: [
      { path: "notifications", element: <NotificationManagementPage /> },
      { path: "users", element: <UserManagementPage /> },
      { path: "universities", element: <UniversityManagementPage /> },
    ],
  },
  { path: "*", element: <Navigate to="/" /> },
]);
